import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import Plot from 'react-plotly.js';
import { selectRTFO } from '../../reducers/rtfoSlice';
import { selectUser } from '../../reducers/userSlice';
import { chartColors } from '../../helpers/chartColors';
import { useTheme } from '@mui/styles';
import { CircularProgress, Typography } from '@mui/material';

export const RTFOPumpdownChart = ({ gridView, editMode, pumpdownData, pumpdownFalloffTimestamps, setPumpdownFalloffTimestamps, postPerfFalloffTimestamps, setPostPerfFalloffTimestamps, isLoadingPumpdownData }) => {

  const theme = useTheme();
  const user = useSelector(selectUser);
  const rtfo = useSelector(selectRTFO);
  const currentRTFO = rtfo?.value;
  const currentStage = currentRTFO?.stageData?.[currentRTFO?.currentStageName];

  const darkMode = user?.value?.darkMode;

  const data = useMemo(() => {
    if (currentStage && pumpdownData) {
      return getChartData();
    };

    function getChartData() {
      try {
        // add traces
        let traces = [];

        const X = [];
        const YRate = [];
        const YPressure = [];
        const YDepth = [];
        const YVoltage = [];
  

        pumpdownData.forEach(d => {
          X.push(d.timestamp);
          YRate.push(d.rate || 0);
          YPressure.push(d.pressure || (d.leftPumpPressure + d.rightPumpPressure) / 2 || 0);
          YDepth.push(d.depth > 0 ? d.depth : null);
          YVoltage.push(d.voltage);
        })

        traces.push(
          { // pump pressure
            x: X,
            y: YPressure,
            mode: 'line',
            type: 'scattergl',
            xaxis: 'x1',
            yaxis: 'y1',
            name: 'Avg. Pump Pressure (MPa)',
            line: {
              width: 2,
              color: chartColors[2],
              opacity: 1
            },
            hoverinfo: editMode && 'none'
          }
        );
        traces.push(
          { // rate
            x: X,
            y: YRate,
            mode: 'line',
            type: 'scattergl',
            xaxis: 'x1',
            yaxis: 'y2',
            name: 'Pumpdown Rate (m3/min)',
            line: {
              width: 2,
              color: chartColors[3],
              opacity: 1
            },
            hoverinfo: editMode && 'none'
          }
        );
        traces.push(
          { // depth
            x: X,
            y: YDepth,
            mode: 'line',
            type: 'scattergl',
            xaxis: 'x1',
            yaxis: 'y3',
            name: 'Depth (m)',
            line: {
              width: 2,
              color: 'black',
              opacity: 1
            },
            hoverinfo: editMode && 'none'
          }
        );
        traces.push(
          { // voltage
            x: X,
            y: YVoltage,
            mode: 'line',
            type: 'scattergl',
            xaxis: 'x1',
            yaxis: 'y4',
            name: 'Voltage',
            line: {
              width: 2,
              color: chartColors[6],
              opacity: 1
            },
            hoverinfo: editMode && 'none'
          }
        );
  
        return traces;
  
      } catch (error) {
        console.log(error);
        return [];
      };
    };
  }, [currentStage, editMode, pumpdownData]);


  const layout = useMemo(() => {
    return {
      yaxis: { // pump pressure y axis
        linecolor: chartColors[2],
        gridcolor: darkMode ? 'grey' : 'lightgrey',
        color: chartColors[2],
        title: {
          text: 'Pumpdown Pressure (MPa)',
          font: {
            size: 12
          },
        },
        anchor: 'x',
        range: [0, 100],
        side: 'left'
      },
      yaxis2: { // rate y axis
        linecolor: chartColors[3],
        showgrid: false,
        color: chartColors[3],
        title: {
          text: 'Pumpdown Rate (m3/min)',
          font: {
            size: 12
          },
        },
        range: [0, 10],
        anchor: 'free',
        overlaying: 'y',
        side: 'left',
        position: 0
      },
      yaxis3: { // depth y axis
        linecolor: 'black',
        showgrid: false,
        color: 'black',
        title: {
          text: 'Depth (m)',
          font: {
            size: 12
          },
        },
        rangemode: 'nonnegative',
        overlaying: 'y',
        side: 'right',
        anchor: 'x'
      },
      yaxis4: { // voltage y axis
        linecolor: chartColors[6],
        showgrid: false,
        color: chartColors[6],
        title: {
          text: 'Voltage',
          font: {
            size: 12
          },
        },
        range: [-1, 1],
        overlaying: 'y',
        side: 'right',
        anchor: 'free',
        position: 1,
        uirevision: 'true'
      },
      xaxis: { // Date x axis
        type: 'date',
        title: {
          text: 'Date',
          font: {
            size: 12
          }
        },
        linecolor: darkMode ? 'lightgrey' : 'black',
        gridcolor: darkMode ? 'grey' : 'lightgrey',
        color: darkMode ? 'lightgrey' : 'black',
        anchor: 'y',
        domain: [(gridView ? 0.1 : 0.05), (gridView ? 0.9 : 0.95)],
        showspikes: !editMode ? true : false,
        spikethickness: 2,
        spikedash: "dot",
        spikecolor: "#999999",
        spikemode: "across",
      },
      hovermode: "x",
      hoverdistance: 10, // Distance to show hover label of data point
      spikedistance: 1000,
      autosize: true,
      showlegend: true,
      legend: {
        orientation: "h",
        yanchor: "top",
        font: {
          color: darkMode ? 'lightgrey' : 'grey',
          size: 10
        },
        y: (gridView ? -0.15 : -0.1),
      },
      shapes: [
        {
          type: 'rect',
          xref: 'x',
          yref: 'paper',
          x0: new Date(pumpdownFalloffTimestamps?.startTimestamp),
          y0: 0,
          x1: new Date(pumpdownFalloffTimestamps?.endTimestamp),
          y1: 1,
          line: {
            width: 0,
          },
          fillcolor: chartColors[0],
          opacity: 0.25
        },
        editMode === 'pumpdownStart' && {
          type: 'line',
          xref: 'x',
          yref: 'paper',
          x0: new Date(pumpdownFalloffTimestamps?.startTimestamp),
          y0: 0,
          x1: new Date(pumpdownFalloffTimestamps?.startTimestamp),
          y1: 1,
          line: {
            width: 3,
            color: chartColors[0],
          },
          opacity: 1
        },
        editMode === 'pumpdownEnd' && {
          type: 'line',
          xref: 'x',
          yref: 'paper',
          x0: new Date(pumpdownFalloffTimestamps?.endTimestamp),
          y0: 0,
          x1: new Date(pumpdownFalloffTimestamps?.endTimestamp),
          y1: 1,
          line: {
            width: 3,
            color: chartColors[0],
          },
          opacity: 1
        },
        {
          type: 'rect',
          xref: 'x',
          yref: 'paper',
          x0: new Date(postPerfFalloffTimestamps?.startTimestamp),
          y0: 0,
          x1: new Date(postPerfFalloffTimestamps?.endTimestamp),
          y1: 1,
          line: {
            width: 0,
          },
          fillcolor: chartColors[6],
          opacity: 0.25
        },
        editMode === 'postPerfStart' && {
          type: 'line',
          xref: 'x',
          yref: 'paper',
          x0: new Date(postPerfFalloffTimestamps?.startTimestamp),
          y0: 0,
          x1: new Date(postPerfFalloffTimestamps?.startTimestamp),
          y1: 1,
          line: {
            width: 3,
            color: chartColors[6],
          },
          opacity: 1
        },
        editMode === 'postPerfEnd' && {
          type: 'line',
          xref: 'x',
          yref: 'paper',
          x0: new Date(postPerfFalloffTimestamps?.endTimestamp),
          y0: 0,
          x1: new Date(postPerfFalloffTimestamps?.endTimestamp),
          y1: 1,
          line: {
            width: 3,
            color: chartColors[6],
          },
          opacity: 1
        },
      ],
      annotations: [
        {
          text: "Pumpdown",
            font: {
            size: 14,
             color: darkMode ? 'lightgrey' : 'black'
          },
          showarrow: false,
          align: 'center',
          x: 0.5, //position in x domain
          y: (gridView ? 1.135 : 1.05), //position in y domain
          xref: 'paper',
          yref: 'paper',
        },
        {
          text: `Pad: ${currentRTFO?.name} | Well: ${currentStage?.wellName} | Stage: ${currentStage?.stageNumber}`,
            font: {
            size: 10,
             color: darkMode ? 'lightgrey' : 'black'
          },
          showarrow: false,
          align: 'center',
          x: 0.5, //position in x domain
          y: (gridView ? 1.08 : 1.02), //position in y domain
          xref: 'paper',
          yref: 'paper',
        },
        {
          x: new Date((pumpdownFalloffTimestamps?.startTimestamp + pumpdownFalloffTimestamps?.endTimestamp) / 2),
          y: 1,
          xref: 'x',
          yref: 'paper',
          text: 'Pumpdown',
          textangle: '-90',
          bgcolor: ('transparent'),
          font: {color: chartColors[0], weight: 'bold'},
          borderpad: 1,
          showarrow: false,
          ax: 0,
          ay: -40
        },
        {
          x: new Date((postPerfFalloffTimestamps?.startTimestamp + postPerfFalloffTimestamps?.endTimestamp) / 2),
          y: 1,
          xref: 'x',
          yref: 'paper',
          text: 'Post Perf',
          textangle: '-90',
          bgcolor: ('transparent'),
          font: {color: chartColors[6], weight: 'bold'},
          borderpad: 1,
          showarrow: false,
          ax: 0,
          ay: -40
        }
      ],
      uirevision: 'true',
      margin: {"t": 60, "l": 60, "r": 60, "b": 50},
      images: [
        {
          x: (gridView ? 0.05 : 0.035),
          y: (gridView ? 1.05 : 1.025),
          sizex: (gridView ? 0.1 : 0.05),
          sizey: 0.1,
          source: process.env.PUBLIC_URL + `/img/ssd_logomark_chart_${darkMode ? 'white' : 'primary'}.png`,
          xref: "page",
          yref: "page",
          xanchor: "right",
          yanchor: "bottom",
          opacity: 0.75
        }
      ],
      plot_bgcolor: "transparent",
      paper_bgcolor: theme?.palette?.elevationOne?.[(darkMode ? 'dark' : 'light')]
    };
  }, [currentRTFO?.name, currentStage?.stageNumber, currentStage?.wellName, darkMode, editMode, gridView, postPerfFalloffTimestamps?.endTimestamp, postPerfFalloffTimestamps?.startTimestamp, pumpdownFalloffTimestamps?.endTimestamp, pumpdownFalloffTimestamps?.startTimestamp, theme?.palette?.elevationOne])

  const config = {
    displaylogo: false,
    toImageButtonOptions: {
      format: 'png', // one of png, svg, jpeg, webp
      filename: "RTFO-ISIP-" + (new Date().toISOString().split('T')[0]),
      height: 550,
      width: 960,
      scale: 1 // Multiply title/legend/axis/canvas sizes by this factor
    }
  };


  return (
    <div style={{height: '100%', width: '100%'}}>
      {isLoadingPumpdownData?.current ?
        <div style={{height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center'}}>
        <Typography style={{marginBottom: 20}}>Downloading pumpdown data...</Typography>
        <CircularProgress />
      </div> :
      <Plot
        data={data}
        layout={layout}
        config={config}
        useResizeHandler={true}
        style={{width: '100%', height: '100%'}}
        onClick={handlePlotClick}
      />}

    </div>
  );

  // FUNCTIONS
  function handlePlotClick(e) {
    try {
      if (editMode === 'pumpdownStart') {
        const newStart = new Date(e.points?.[0]?.x)?.getTime();
        setPumpdownFalloffTimestamps(prev => ({...prev, startTimestamp: newStart}));
      } else if (editMode === 'pumpdownEnd') {
        const newEnd = new Date(e.points?.[0]?.x)?.getTime();
        setPumpdownFalloffTimestamps(prev => ({...prev, endTimestamp: newEnd}));
      } else if (editMode === 'postPerfStart') {
        const newStart = new Date(e.points?.[0]?.x)?.getTime();
        setPostPerfFalloffTimestamps(prev => ({...prev, startTimestamp: newStart}));
      } else if (editMode === 'postPerfEnd') {
        const newEnd = new Date(e.points?.[0]?.x)?.getTime();
        setPostPerfFalloffTimestamps(prev => ({...prev, endTimestamp: newEnd}));
      };

    } catch (error) {
      console.log(error);
    };
  };
};
